// src/pages/finance-portal/reports/BurnRunway.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, TextField, Alert, Grid,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Card, CardContent, CircularProgress, Stack, Slider,
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';
import axiosInstance from '../../../utils/axiosInstance';
import {
    fontFamily, ACCENT, formatCurrency, todayIso,
} from './reportShared';

export default function BurnRunway() {
    const [end, setEnd] = useState(todayIso());
    const [scenarioAdj, setScenarioAdj] = useState(0);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axiosInstance.get('/api/finance/analytics/burn-runway', {
                params: { end_date: end, scenario_adjustment: scenarioAdj },
            });
            if (res.data.success) setData(res.data);
            else setError(res.data.message || 'Failed');
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load burn/runway');
        } finally { setLoading(false); }
    }, [end, scenarioAdj]);

    useEffect(() => { load(); }, [load]);

    const MetricCard = ({ label, value, highlight }) => (
        <Card variant="outlined">
            <CardContent>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700, color: highlight ? ACCENT : 'text.primary' }}>
                    {value}
                </Typography>
            </CardContent>
        </Card>
    );

    return (
        <Box sx={{ p: 3, fontFamily }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SpeedIcon sx={{ color: ACCENT }} />
                <Typography variant="h5" sx={{ fontFamily, fontWeight: 700 }}>Burn Rate &amp; Runway</Typography>
            </Stack>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                        <TextField label="As of" type="date" fullWidth size="small"
                            InputLabelProps={{ shrink: true }}
                            value={end} onChange={(e) => setEnd(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">
                            Scenario adjustment: {scenarioAdj >= 0 ? '+' : ''}{scenarioAdj}% burn
                        </Typography>
                        <Slider value={scenarioAdj}
                            onChange={(_, v) => setScenarioAdj(v)}
                            min={-50} max={100} step={5}
                            marks={[
                                { value: -50, label: '-50%' },
                                { value: 0, label: '0%' },
                                { value: 100, label: '+100%' },
                            ]} />
                    </Grid>
                    <Grid item xs={12} md={2} sx={{ textAlign: 'right' }}>
                        <Button onClick={load} variant="contained"
                            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: '#15803d' } }}>Update</Button>
                    </Grid>
                </Grid>
            </Paper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

            {data && !loading && (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={3}>
                            <MetricCard label="Cash Balance" highlight
                                value={formatCurrency(data.cash_balance)} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MetricCard label="3-mo Burn" value={formatCurrency(data.burn_rate['3_month'])} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MetricCard label="6-mo Burn" value={formatCurrency(data.burn_rate['6_month'])} />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <MetricCard label="Runway" highlight
                                value={`${data.runway_months} mo`} />
                        </Grid>
                    </Grid>

                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Scenario Projection ({data.scenario.adjustment_percent}% adj)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Adjusted burn: {formatCurrency(data.scenario.adjusted_burn)} /month ·
                            Runway: {data.scenario.adjusted_runway_months} months
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Months From Now</TableCell>
                                        <TableCell align="right">Projected Cash</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.scenario.projection.map((p) => (
                                        <TableRow key={p.month_offset}>
                                            <TableCell>M+{p.month_offset}</TableCell>
                                            <TableCell align="right"
                                                sx={{ color: p.projected_cash < 0 ? '#ef4444' : 'inherit' }}>
                                                {formatCurrency(p.projected_cash)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </>
            )}
        </Box>
    );
}
